import { Container } from "pixi.js";

type ScrollDirection = "vertical" | "horizontal";

export interface VirtualListOptions {
  itemCount: number;
  itemSize: number | ((index: number) => number);
  overscan?: number;
  initialIndex?: number;
  direction?: ScrollDirection;
  viewportSize?: number;
}

export interface VirtualItem {
  index: number;
  start: number;
  size: number;
  isVisible: boolean;
}

export type VirtualListChangeCallback = (virtualItems: VirtualItem[]) => void;

export class VirtualListManager {
  private itemCount: number;
  private itemSize: number | ((index: number) => number);
  private overscan: number;
  private direction: ScrollDirection;
  private viewportSize?: number;

  private scrollOffset: number = 0;
  private containerSize: number = 0;
  private positionsReady: boolean = false;

  private containerRef: Container | null = null;
  private scrollRef: Container | null = null;
  private itemSizes: number[] = [];
  private itemPositions: number[] = [];

  private changeCallbacks: Set<VirtualListChangeCallback> = new Set();

  private isVertical: boolean;
  private scrollProperty: "x" | "y";

  constructor(options: VirtualListOptions) {
    this.itemCount = options.itemCount;
    this.itemSize = options.itemSize;
    this.overscan = options.overscan ?? 3;
    this.direction = options.direction ?? "vertical";
    this.viewportSize = options.viewportSize;

    this.isVertical = this.direction === "vertical";
    this.scrollProperty = this.isVertical ? "y" : "x";

    this.containerSize = options.viewportSize || 0;

    // Initialize positions
    this.calculateItemSizesAndPositions();
    this.positionsReady = this.itemCount > 0;

    // Set initial scroll position
    if (this.positionsReady && this.itemCount > 0) {
      const initialIndex = options.initialIndex ?? 0;
      this.scrollToIndex(initialIndex, false);
    }
  }

  /**
   * Update the item count
   */
  updateItemCount(itemCount: number): void {
    if (this.itemCount !== itemCount) {
      this.itemCount = itemCount;
      this.calculateItemSizesAndPositions();
      this.positionsReady = this.itemCount > 0;
      this.notifyChange();
    }
  }

  /**
   * Update the item size function or value
   */
  updateItemSize(itemSize: number | ((index: number) => number)): void {
    this.itemSize = itemSize;
    this.calculateItemSizesAndPositions();
    this.notifyChange();
  }

  /**
   * Update viewport size
   */
  updateViewportSize(viewportSize?: number): void {
    if (this.viewportSize !== viewportSize) {
      this.viewportSize = viewportSize;
      if (viewportSize !== undefined) {
        this.containerSize = viewportSize;
      } else if (this.scrollRef) {
        this.containerSize = this.isVertical
          ? this.scrollRef.height
          : this.scrollRef.width;
      }
      this.notifyChange();
    }
  }

  /**
   * Set container reference
   */
  setContainerRef(container: Container | null): void {
    this.containerRef = container;
  }

  /**
   * Set scroll reference and update container size if needed
   */
  setScrollRef(scroll: Container | null): void {
    this.scrollRef = scroll;
    if (!this.viewportSize && scroll) {
      const newSize = this.isVertical ? scroll.height : scroll.width;
      if (this.containerSize !== newSize) {
        this.containerSize = newSize;
        this.notifyChange();
      }
    }
  }

  /**
   * Calculate item sizes and positions
   */
  private calculateItemSizesAndPositions(): void {
    const sizes: number[] = [];
    const positions: number[] = [];
    let currentPosition = 0;

    for (let i = 0; i < this.itemCount; i++) {
      const size =
        typeof this.itemSize === "function" ? this.itemSize(i) : this.itemSize;
      sizes[i] = size;
      positions[i] = currentPosition;
      currentPosition += size;
    }

    this.itemSizes = sizes;
    this.itemPositions = positions;
  }

  /**
   * Find the index at a given offset
   */
  private findIndexAtOffset(offset: number): number {
    const positions = this.itemPositions;
    const localSizes = this.itemSizes;

    if (positions.length === 0 || localSizes.length === 0) return 0;
    if (offset < 0) return 0;

    let low = 0;
    let high = positions.length - 1;

    // Binary search
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const position = positions[mid];
      const size = localSizes[mid];

      if (position === undefined || size === undefined) return low;

      if (offset >= position && offset < position + size) {
        return mid;
      }
      if (offset < position) {
        high = mid - 1;
      } else {
        low = mid + 1;
      }
    }
    return Math.max(0, Math.min(low, positions.length - 1));
  }

  /**
   * Calculate visible range
   */
  private calculateVisibleRange(): { startIndex: number; endIndex: number } {
    if (!this.positionsReady) {
      return { startIndex: 0, endIndex: 0 };
    }

    const startIndex = Math.max(
      0,
      this.findIndexAtOffset(this.scrollOffset) - this.overscan
    );

    const endIndex = Math.min(
      this.itemCount - 1,
      this.findIndexAtOffset(this.scrollOffset + this.containerSize) +
        this.overscan
    );

    return { startIndex, endIndex };
  }

  /**
   * Generate virtual items
   */
  getVirtualItems(): VirtualItem[] {
    if (
      !this.positionsReady ||
      this.itemCount === 0 ||
      this.itemPositions.length === 0 ||
      this.itemSizes.length === 0
    ) {
      return [];
    }

    const { startIndex, endIndex } = this.calculateVisibleRange();

    if (startIndex > endIndex) {
      return [];
    }

    const items: VirtualItem[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      if (i < 0 || i >= this.itemPositions.length || i >= this.itemSizes.length)
        continue;
      items.push({
        index: i,
        start: this.itemPositions[i],
        size: this.itemSizes[i],
        isVisible: true,
      });
    }
    return items;
  }

  /**
   * Get total size of all items
   */
  getTotalSize(): number {
    if (
      !this.positionsReady ||
      this.itemCount === 0 ||
      this.itemPositions.length === 0 ||
      this.itemSizes.length === 0
    )
      return 0;

    const lastItemIndex = this.itemCount - 1;
    if (
      lastItemIndex < 0 ||
      lastItemIndex >= this.itemPositions.length ||
      lastItemIndex >= this.itemSizes.length
    )
      return 0;

    return this.itemPositions[lastItemIndex] + this.itemSizes[lastItemIndex];
  }

  /**
   * Update scroll offset
   */
  updateScroll(newOffset: number, notify: boolean = true): void {
    if (this.scrollOffset !== newOffset) {
      this.scrollOffset = newOffset;
      if (this.containerRef) {
        this.containerRef[this.scrollProperty] = -newOffset;
      }
      if (notify) {
        this.notifyChange();
      }
    }
  }

  /**
   * Scroll to specific index
   */
  scrollToIndex(index: number, notify: boolean = true): void {
    if (
      this.itemPositions.length > 0 &&
      index >= 0 &&
      index < this.itemPositions.length
    ) {
      const targetOffset = this.itemPositions[index];
      this.updateScroll(targetOffset, notify);
    } else if (this.itemCount > 0) {
      this.updateScroll(0, notify);
    }
  }

  /**
   * Get current scroll offset
   */
  getScrollOffset(): number {
    return this.scrollOffset;
  }

  /**
   * Get item sizes array
   */
  getItemSizes(): readonly number[] {
    return this.itemSizes;
  }

  /**
   * Get item positions array
   */
  getItemPositions(): readonly number[] {
    return this.itemPositions;
  }

  /**
   * Get container reference
   */
  getContainerRef(): Container | null {
    return this.containerRef;
  }

  /**
   * Get scroll reference
   */
  getScrollRef(): Container | null {
    return this.scrollRef;
  }

  /**
   * Get direction
   */
  getDirection(): ScrollDirection {
    return this.direction;
  }

  /**
   * Subscribe to changes in virtual items
   */
  subscribe(callback: VirtualListChangeCallback): () => void {
    this.changeCallbacks.add(callback);
    return () => {
      this.changeCallbacks.delete(callback);
    };
  }

  /**
   * Notify all subscribers of changes
   */
  private notifyChange(): void {
    const virtualItems = this.getVirtualItems();
    this.changeCallbacks.forEach((callback) => {
      callback(virtualItems);
    });
  }

  /**
   * Destroy the manager and clean up
   */
  destroy(): void {
    this.changeCallbacks.clear();
    this.containerRef = null;
    this.scrollRef = null;
  }
}
